import { create } from "zustand";
import { apiClient } from "@/api/client";
import { AxiosError } from "axios";

export interface NotificationPreferenceItem {
  action: string;
  title: string;
  description: string;
  enabled: boolean;
  default_enabled: boolean;
}

// Dvippatel: Added consistent actor interface for notifications
export interface NotificationActor {
  id: string;
  name: string;
  avatar_url_signed?: string | null;
  avatar_updated_at?: string | null;
}


export interface NotificationPreferenceGroup {
  group_key: string;
  display: string;
  enabled: boolean;
  items: NotificationPreferenceItem[];
}

export interface NotificationPreferences {
  emailNotifications?: boolean;
  showUnreadCount?: boolean;
  groups?: NotificationPreferenceGroup[];
  [key: string]: any;
}

export interface NotificationDetail {
  id: string;
  group_key: string;
  action: string;
  title: string;
  message: string;
  entity_type: string;
  entity_id: string;
  actor_id: string;
  metadata: {
    new_values?: any;
    previous_values?: any;
    [key: string]: any;
  };
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

// Dvippatel: Modified Notification interface to include consistent actor
// export interface Notification {
//   id: string;
//   notification_id: string;
//   user_id: string;
//   is_read: boolean;
//   read_at: string | null;
//   is_deleted: boolean;
//   delivery_status: string;
//   created_at: string;
//   updated_at: string;
//   deleted_at: string | null;
//   notification: NotificationDetail;
// }

// Proposed Change
export interface Notification {
  id: string;
  notification_id: string;
  user_id: string;

  is_read: boolean;
  read_at: string | null;
  is_deleted: boolean;
  delivery_status: string;

  created_at: string;
  updated_at: string;
  deleted_at: string | null;

  // 👇 flattened fields (IMPORTANT)
  group_key: string;
  action: string;
  display_title: string;
  display_message: string;
  entity_type: string;
  entity_id: string;
  metadata: Record<string, any>;

  actor?: NotificationActor | null;
}

// End Proposed Change

export interface NotificationResponse {
  data: Notification[];
  meta: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
  };
}

interface NotificationState {
  preferences: NotificationPreferences | null;
  notifications: Notification[];
  isLoading: boolean;
  unreadCount: number;

  getPreferences: () => Promise<void>;
  setPreferences: (preferences: NotificationPreferences) => void;
  toggleGroup: (groupKey: string) => void;
  toggleItem: (groupKey: string, action: string) => void;
  getNotifications: () => Promise<void>;
  markAllAsRead: () => Promise<void>;
  markAsRead: (notificationId: string) => void;
}

const useNotificationStore = create<NotificationState>((set, get) => ({
  preferences: null,
  notifications: [],
  isLoading: false,
  unreadCount: 0,

  getPreferences: async () => {
    set({ isLoading: true });
    try {
      const response = await apiClient.get<any>("/notification/preferences");

      // Handle different possible response structures
      let preferencesData: NotificationPreferences;

      // Check if response.data is an array (the groups directly)
      if (Array.isArray(response.data)) {
        preferencesData = {
          groups: response.data,
        };
      }
      // Check if response.data.data exists
      else if (response.data?.data) {
        if (Array.isArray(response.data.data)) {
          preferencesData = {
            groups: response.data.data,
          };
        } else {
          preferencesData = response.data.data;
        }
      }
      // Use response.data directly
      else {
        preferencesData = response.data || {};
      }

      // Log for debugging
      console.log("Notification Preferences Response:", response.data);
      console.log("Parsed Preferences:", preferencesData);

      set({
        preferences: preferencesData,
        isLoading: false,
      });
    } catch (error) {
      set({ isLoading: false });
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.message || "Failed to fetch notification preferences";
      throw new Error(errorMessage);
    }
  },

  setPreferences: (preferences: NotificationPreferences) => {
    set({ preferences });
    // Store in localStorage for persistence
    localStorage.setItem("notificationPreferences", JSON.stringify(preferences));
  },

  toggleGroup: (groupKey: string) => {
    const currentPreferences = get().preferences;
    if (!currentPreferences?.groups) return;

    const updatedGroups = currentPreferences.groups.map((group) => {
      if (group.group_key === groupKey) {
        const newEnabled = !group.enabled;
        return {
          ...group,
          enabled: newEnabled,
          items: group.items.map((item) => ({
            ...item,
            enabled: newEnabled,
          })),
        };
      }
      return group;
    });

    const updatedPreferences = {
      ...currentPreferences,
      groups: updatedGroups,
    };

    get().setPreferences(updatedPreferences);
  },

  toggleItem: (groupKey: string, action: string) => {
    const currentPreferences = get().preferences;
    if (!currentPreferences?.groups) return;

    const updatedGroups = currentPreferences.groups.map((group) => {
      if (group.group_key === groupKey) {
        const updatedItems = group.items.map((item) => {
          if (item.action === action) {
            return { ...item, enabled: !item.enabled };
          }
          return item;
        });

        // Check if all items are enabled to update group enabled state
        const allEnabled = updatedItems.every((item) => item.enabled);
        const anyEnabled = updatedItems.some((item) => item.enabled);

        return {
          ...group,
          items: updatedItems,
          enabled: anyEnabled, // Group enabled if any item is enabled
        };
      }
      return group;
    });

    const updatedPreferences = {
      ...currentPreferences,
      groups: updatedGroups,
    };

    get().setPreferences(updatedPreferences);
  },

  getNotifications: async () => {
    set({ isLoading: true });
    try {
      const response = await apiClient.get<NotificationResponse>("/notification");

      // Get notifications from response
      const notificationsData = response.data?.data || [];

      // Calculate unread count
      const unread = notificationsData.filter((n) => !n.is_read).length;

      set({
        notifications: notificationsData,
        unreadCount: unread,
        isLoading: false,
      });
    } catch (error) {
      set({ isLoading: false });
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.message || "Failed to fetch notifications";
      throw new Error(errorMessage);
    }
  },

  markAllAsRead: async () => {
    set({ isLoading: true });
    try {
      await apiClient.post("/notification/read-all");

      // Update local state
      const updatedNotifications = get().notifications.map((notification) => ({
        ...notification,
        is_read: true,
        read_at: new Date().toISOString(),
      }));

      set({
        notifications: updatedNotifications,
        unreadCount: 0,
        isLoading: false,
      });
    } catch (error) {
      set({ isLoading: false });
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.message || "Failed to mark all as read";
      throw new Error(errorMessage);
    }
  },

  markAsRead: (notificationId: string) => {
    const updatedNotifications = get().notifications.map((notification) =>
      notification.id === notificationId
        ? { ...notification, is_read: true, read_at: new Date().toISOString() }
        : notification
    );

    const unread = updatedNotifications.filter((n) => !n.is_read).length;

    set({
      notifications: updatedNotifications,
      unreadCount: unread,
    });
  },
}));

export default useNotificationStore;
